import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import FunnelLayout from "./FunnelLayout";
import "./CommonNewFunnel.css";

import PaceOkImg from "../../assets/images/pace-ok.png";
import PaceFasterImg from "../../assets/images/pace-faster.png";
import PaceTooFastImg from "../../assets/images/pace-too-fast.png";

const LS_KEY = "glp1_intake_step3";
const STEP1_KEY = "glp1_intake_step1";

type PaceValue = "" | "works" | "faster" | "too_fast";

type Step3State = {
  pace: PaceValue; // ✅ INTERNAL ONLY (not saved)
};

type Step1Saved = {
  weight?: string;
  goalWeight?: string;
};

type Step3Saved = {
  // ✅ ONLY full answer stored (what you want)
  Q4_How_is_that_pace_for_you: string;

  // calculations
  weeklyLow: number;
  weeklyHigh: number;
  weeksWorks: number;
  weeksFaster: number;
  goalWeightDisplay: string;
};

const DEFAULT: Step3State = { pace: "" };

const QUESTION_KEY = "Q4_How_is_that_pace_for_you";

const PACE_LABEL: Record<Exclude<PaceValue, "">, string> = {
  works: "That works for me",
  faster: "I want it faster",
  too_fast: "That’s too fast",
};

const fullAnswerToValue = (answer: string): PaceValue => {
  if (answer === PACE_LABEL.works) return "works";
  if (answer === PACE_LABEL.faster) return "faster";
  if (answer === PACE_LABEL.too_fast) return "too_fast";
  return "";
};

type PaceCardProps = {
  label: string;
  checked: boolean;
  onChange: () => void;
  imgSrc: string;
  imgAlt: string;
};

const PaceCard: React.FC<PaceCardProps> = ({
  label,
  checked,
  onChange,
  imgSrc,
  imgAlt,
}) => {
  return (
    <button
      type="button"
      className={`pace-card ${checked ? "is-selected" : ""}`}
      onClick={onChange}
    >
      <div className="pace-card__icon">
        <img src={imgSrc} alt={imgAlt} />
      </div>

      <div className="pace-card__bottom">
        <span className={`pace-radio ${checked ? "is-selected" : ""}`}>
          {checked && <span className="dot" />}
        </span>
        <span className="pace-card__label">{label}</span>
      </div>

      <input className="sr-only" type="radio" checked={checked} readOnly />
    </button>
  );
};

const NewFunnelStep3: React.FC = () => {
  const navigate = useNavigate();
  const [state, setState] = useState<Step3State>(DEFAULT);

  const calc = useMemo(() => {
    let weight = 0;
    let goalWeight = 0;

    const saved = localStorage.getItem(STEP1_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved) as Step1Saved;
        weight = Number(parsed?.weight || 0);
        goalWeight = Number(parsed?.goalWeight || 0);
      } catch (e){
        console.log(e);
      }
    }

    const delta = Math.max(0, weight - goalWeight);

    const lowPerWeek = weight ? weight * 0.015 : 0; // 1.5%
    const highPerWeek = weight ? weight * 0.02 : 0; // 2%

    const weeksWorksRaw = delta && lowPerWeek ? delta / lowPerWeek : 0;

    const fasterRate = 0.01644;
    const fasterPerWeek = weight ? weight * fasterRate : 0;
    const weeksFasterRaw = delta && fasterPerWeek ? delta / fasterPerWeek : 0;

    const round2 = (n: number) =>
      Number.isFinite(n) ? Number((Math.round(n * 100) / 100).toFixed(2)) : 0;

    return {
      weeklyLow: round2(lowPerWeek),
      weeklyHigh: round2(highPerWeek),
      weeksWorks: round2(weeksWorksRaw),
      weeksFaster: round2(weeksFasterRaw),
      goalWeightDisplay: goalWeight ? String(goalWeight) : "--",
    };
  }, []);

  // ✅ HYDRATE: read FULL answer from LS and map to internal value
  useEffect(() => {
    const saved = localStorage.getItem(LS_KEY);
    if (!saved) return;

    try {
      const parsed = JSON.parse(saved) as Partial<Step3Saved>;
      const full = String(parsed?.[QUESTION_KEY] || "");
      const pace = fullAnswerToValue(full);
      if (pace) setState({ pace });
    } catch (e){
      console.log(e);
    }
  }, []);

  // ✅ SAVE: store ONLY full answer (no extra “value” key)
  const save = (pace: Exclude<PaceValue, "">) => {
    setState({ pace });

    const payload: Step3Saved = {
      [QUESTION_KEY]: PACE_LABEL[pace], // ✅ ONLY FULL ANSWER STORED
      weeklyLow: calc.weeklyLow,
      weeklyHigh: calc.weeklyHigh,
      weeksWorks: calc.weeksWorks,
      weeksFaster: calc.weeksFaster,
      goalWeightDisplay: calc.goalWeightDisplay,
    };

    localStorage.setItem(LS_KEY, JSON.stringify(payload));
  };

  const canGoNext = Boolean(state.pace);

  const next = () => {
    if (!canGoNext) return;
    navigate("/glp1/intake-form/step-3");
  };

  return (
    <FunnelLayout>
      <div className="pace-wrap">
        <h1 className="pace-title">
          With medication,
          <br />
          you&apos;ll lose {calc.weeklyLow} to {calc.weeklyHigh} pounds{" "}
          <span className="pace-title__blue">per Week.</span>
        </h1>

        <div className="pace-divider" />

        <div className="pace-subtext">
          It will take about {calc.weeksWorks} weeks to
          <br />
          reach your goal weight of {calc.goalWeightDisplay}.
        </div>

        <div className="pace-divider" />

        <div className="pace-question">How is that pace for you?</div>

        <div className="pace-cards">
          <PaceCard
            label={PACE_LABEL.works}
            imgSrc={PaceOkImg}
            imgAlt="Works for me"
            checked={state.pace === "works"}
            onChange={() => save("works")}
          />

          <PaceCard
            label={PACE_LABEL.faster}
            imgSrc={PaceTooFastImg}
            imgAlt="Want it faster"
            checked={state.pace === "faster"}
            onChange={() => save("faster")}
          />

          <PaceCard
            label={PACE_LABEL.too_fast}
            imgSrc={PaceFasterImg }
            imgAlt="Too fast"
            checked={state.pace === "too_fast"}
            onChange={() => save("too_fast")}
          />
        </div>

        <button
          className={`next-btn ${!canGoNext ? "is-disabled" : ""}`}
          onClick={next}
          disabled={!canGoNext}
        >
          Next →
        </button>
      </div>
    </FunnelLayout>
  );
};

export default NewFunnelStep3;